// File: src/components/admin/AdminMarketsModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function adminConfig() {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
    },
  };
}

const idOf = (value) => String(value?._id || value || '');
const placeLabel = (place) => [
  place.locality, place.county, place.state,
  String(place.countryCode || '').toUpperCase(),
].filter(Boolean).join(', ');
const errorText = (error) => error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

export default function AdminMarketsModal({ open, onClose, onChanged }) {
  const [markets, setMarkets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [places, setPlaces] = useState([]);
  const [newName, setNewName] = useState('');
  const [rename, setRename] = useState('');
  const [query, setQuery] = useState('');
  const [resolved, setResolved] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(false);
  const sessionRef = useRef(0);
  closeRef.current = onClose;
  const selected = markets.find((market) => idOf(market) === selectedId);

  useEffect(() => {
    if (!open) return undefined;
    const session = ++sessionRef.current;
    setNotice('');
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busyRef.current) {
        event.preventDefault();
        closeRef.current?.();
      }
      if (event.key !== 'Tab') return;
      const controls = [...(dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'
      ) || [])];
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); dialogRef.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      if (sessionRef.current === session) sessionRef.current += 1;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoadingMarkets(true);
    setError('');
    setResolved(null);
    setPendingMove(null);
    axios.get(`${API_URL}/admin/markets`, adminConfig()).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data)) throw new Error('The markets API must return an array.');
      setMarkets(data);
      setSelectedId((current) => data.some((market) => idOf(market) === current) ? current : idOf(data[0]));
    }).catch((err) => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoadingMarkets(false); });
    return () => { active = false; };
  }, [open, reload]);

  useEffect(() => {
    setPlaces([]);
    setResolved(null);
    setPendingMove(null);
    if (!open || !selectedId) { setLoadingPlaces(false); return undefined; }
    let active = true;
    setLoadingPlaces(true);
    axios.get(`${API_URL}/admin/markets/${selectedId}/places`, adminConfig()).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data)) throw new Error('The places API must return an array.');
      setPlaces(data);
    }).catch((err) => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoadingPlaces(false); });
    return () => { active = false; };
  }, [open, selectedId, reload]);

  useEffect(() => { setRename(selected?.name || ''); }, [selected?.name, selectedId]);

  async function run(action) {
    if (busyRef.current) return;
    const session = sessionRef.current;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action(() => sessionRef.current === session);
    } catch (err) {
      if (sessionRef.current === session) setError(errorText(err));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function assign(place, targetId, expectedId) {
    await run(async (isCurrent) => {
      const body = place._id
        ? { placeId: String(place._id), expectedMarketId: expectedId }
        : {
            latitude: place.latitude,
            longitude: place.longitude,
            expectedMarketId: expectedId,
          };
      const { data: result } = await axios.post(
        `${API_URL}/admin/markets/${targetId}/places`,
        body,
        adminConfig()
      );
      onChanged?.({ type: 'place-assigned', result });
      if (!isCurrent()) return;
      setResolved(null);
      setPendingMove(null);
      setQuery('');
      setReload((value) => value + 1);
      setNotice(`Grouping saved. ${Number(result?.updatedEvents || 0)} existing events updated.`);
    });
  }

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div style={S.backdrop} onClick={(event) => {
      if (event.target === event.currentTarget && !busyRef.current) onClose?.();
    }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label="Manage markets" tabIndex={-1} style={S.dialog}>
        <header style={S.header}>
          <h2 style={S.title}>Manage markets</h2>
          <button type="button" disabled={busy} onClick={onClose} style={S.button}>Close</button>
        </header>
        <p style={S.description}>Create markets and choose which places belong to each one.</p>
        {error && <div role="alert" style={S.error}>{error} <button type="button" disabled={busy} onClick={() => setReload((value) => value + 1)} style={S.button}>Reload</button></div>}
        {notice && <p role="status" style={S.notice}>{notice}</p>}
        <fieldset disabled={busy || loadingMarkets} style={S.fieldset}>
          <form style={S.block} onSubmit={(event) => {
            event.preventDefault();
            if (!newName.trim()) return;
            run(async (isCurrent) => {
              const { data: market } = await axios.post(
                `${API_URL}/admin/markets`,
                { name: newName.trim() },
                adminConfig()
              );
              if (!market?._id) throw new Error('The create API must return the saved market.');
              onChanged?.({ type: 'market-created', market });
              if (!isCurrent()) return;
              setMarkets((current) => [...current, market].sort((a, b) => a.name.localeCompare(b.name)));
              setSelectedId(idOf(market));
              setNewName('');
              setNotice('Market created. It becomes visible to members when it has an eligible published gathering.');
            });
          }}>
            <label style={S.label}>New market
              <input required maxLength={120} value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="e.g. Los Angeles" style={S.input} />
            </label>
            <button type="submit" disabled={!newName.trim()} style={S.button}>Create market</button>
          </form>
          <label style={S.label}>View market
            <select value={selectedId} disabled={!markets.length} onChange={(event) => { setSelectedId(event.target.value); setError(''); setNotice(''); }} style={S.input}>
              <option value="">{loadingMarkets ? 'Loading markets…' : 'Select a market'}</option>
              {markets.map((market) => <option key={idOf(market)} value={idOf(market)}>{market.name}</option>)}
            </select>
          </label>
          {!loadingMarkets && !markets.length && <p style={S.description}>No markets yet. Create your first market above.</p>}
          {selected && <>
            <form style={S.block} onSubmit={(event) => {
              event.preventDefault();
              if (!rename.trim() || rename.trim() === selected.name) return;
              run(async (isCurrent) => {
                const { data: market } = await axios.put(
                  `${API_URL}/admin/markets/${selectedId}`,
                  { name: rename.trim() },
                  adminConfig()
                );
                onChanged?.({ type: 'market-renamed', market });
                if (!isCurrent()) return;
                setMarkets((current) => current.map((item) => idOf(item) === selectedId ? market : item));
                setNotice('Market name updated.');
              });
            }}>
              <label style={S.label}>Market name
                <input required maxLength={120} value={rename} onChange={(event) => setRename(event.target.value)} style={S.input} />
              </label>
              <button type="submit" disabled={!rename.trim() || rename.trim() === selected.name} style={S.button}>Rename</button>
            </form>
            <h3 style={S.subtitle}>Places in {selected.name}</h3>
            {loadingPlaces ? <p role="status">Loading places…</p> : places.length ? <ul style={S.list}>
              {places.map((place) => <li key={place.key || idOf(place)} style={S.placeRow}>
                <span>{placeLabel(place)}</span>
                <button type="button" onClick={() => setPendingMove({ place, targetId: '' })} style={S.button}>Move</button>
              </li>)}
            </ul> : <p style={S.description}>No places assigned yet.</p>}
            {pendingMove && <div style={S.panel}>
              <strong>Move {pendingMove.place.locality}</strong>
              <label style={S.label}>Destination market
                <select value={pendingMove.targetId} onChange={(event) => setPendingMove({ ...pendingMove, targetId: event.target.value })} style={S.input}>
                  <option value="">Select a market</option>
                  {markets.filter((market) => idOf(market) !== selectedId).map((market) => <option key={idOf(market)} value={idOf(market)}>{market.name}</option>)}
                </select>
              </label>
              <p style={S.description}>This updates existing events that follow this place grouping and future address assignments. Manual event overrides stay unchanged.</p>
              <div style={S.actions}>
                <button type="button" disabled={!pendingMove.targetId} onClick={() => assign(pendingMove.place, pendingMove.targetId, selectedId)} style={S.button}>Save grouping</button>
                <button type="button" onClick={() => setPendingMove(null)} style={S.button}>Cancel</button>
              </div>
              {markets.length < 2 && <p style={S.description}>Create another market above to separate this place.</p>}
            </div>}
            <form style={S.block} onSubmit={(event) => {
              event.preventDefault();
              if (!query.trim()) return;
              run(async (isCurrent) => {
                const { data: result } = await axios.post(
                  `${API_URL}/admin/markets/resolve-address`,
                  { address: query.trim() },
                  adminConfig()
                );
                if (!result?.place?.locality || !result?.place?.state || !result?.place?.countryCode) throw new Error('Could not resolve a complete place. Try a specific address in that city.');
                if (!Number.isFinite(result.latitude) || !Number.isFinite(result.longitude)) {
                  throw new Error('Could not resolve coordinates. Try a more complete address.');
                }
                if (isCurrent()) setResolved({
                  ...result,
                  place: {
                    ...result.place,
                    latitude: result.latitude,
                    longitude: result.longitude,
                  },
                });
              });
            }}>
              <label style={S.label}>Add a place
                <input required value={query} onChange={(event) => { setQuery(event.target.value); setResolved(null); }} placeholder="City and state, or an address" style={S.input} />
              </label>
              <button type="submit" disabled={!query.trim()} style={S.button}>Find place</button>
            </form>
            {resolved && <div style={S.panel}>
              <strong>{placeLabel(resolved.place)}</strong>
              <p style={S.description}>{resolved.market ? `Currently grouped in ${resolved.market.name}.` : 'Not assigned to a market yet.'}</p>
              {idOf(resolved.market) === selectedId ? <p>This place already belongs to {selected.name}.</p> : <>
                <p style={S.description}>Assigning this place also updates existing events that follow its grouping. Manual event overrides stay unchanged.</p>
                <button type="button" onClick={() => assign(resolved.place, selectedId, resolved.market ? idOf(resolved.market) : null)} style={S.button}>Assign to {selected.name}</button>
              </>}
            </div>}
          </>}
        </fieldset>
        {busy && <p role="status">Working…</p>}
      </section>
    </div>, document.body
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  dialog: { width: '100%', maxWidth: 680, maxHeight: '90dvh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxSizing: 'border-box', fontFamily: 'Inter, system-ui, sans-serif', color: '#171717' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  title: { fontSize: 22, margin: 0 },
  subtitle: { fontSize: 17, margin: '24px 0 12px' },
  description: { color: '#555', fontSize: 14, lineHeight: 1.5 },
  fieldset: { border: 0, padding: 0, margin: 0, minWidth: 0 },
  block: { display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, margin: '20px 0' },
  label: { display: 'flex', flexDirection: 'column', gap: 7, fontSize: 14, flex: '1 1 220px', minWidth: 0 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: 8, font: 'inherit', boxSizing: 'border-box', background: '#fff', color: '#171717', marginTop: 4 },
  button: { padding: '10px 14px', border: '1px solid #ccc', borderRadius: 8, background: '#f7f7f7', color: '#171717', font: 'inherit', fontSize: 14, cursor: 'pointer' },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  placeRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderBottom: '1px solid #eee', fontSize: 14 },
  panel: { padding: 16, border: '1px solid #ddd', borderRadius: 10, marginTop: 16 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  error: { color: '#a11919', background: '#fff0f0', padding: 12, borderRadius: 8, marginTop: 16 },
  notice: { color: '#175b35', background: '#eef8f1', padding: 12, borderRadius: 8 },
};
